import React, { useState, useEffect } from 'react';
import {
  X, Search, Plus, Loader2, ChevronDown, ChevronRight, Trash2, Check,
} from 'lucide-react';
import { rulesApi } from '../../services/api';
import type { CatalogRule, CatalogRuleArg, SuggestedRule } from '../../types';

interface Props {
  open: boolean;
  onClose: () => void;
  onAddRules: (rules: SuggestedRule[]) => void;
  columns: string[];
  tableName: string;
}

// ─── Catalog browser step ───

const AddRuleModal: React.FC<Props> = ({ open, onClose, onAddRules, columns, tableName }) => {
  const [step, setStep] = useState<'browse' | 'configure'>('browse');
  const [catalogRules, setCatalogRules] = useState<CatalogRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedRules, setSelectedRules] = useState<CatalogRule[]>([]);
  const [configuredArgs, setConfiguredArgs] = useState<Record<number, Record<string, any>>>({});
  const [expandedRule, setExpandedRule] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      fetchRules();
    }
  }, [open, page, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRules = async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page,
        items_per_page: 20,
        rule_type: 'V',
        sort_col: 'rule',
        sort_dir: 'asc',
      };
      if (searchQuery.trim()) params.search = searchQuery.trim();
      const resp = await rulesApi.list(params);
      const payload = resp.data.payload;
      const rules = payload?.rules || payload || [];
      setCatalogRules(Array.isArray(rules) ? rules : []);
      const pagination = payload?.pagination;
      if (pagination) {
        setTotalPages(pagination.pages || Math.ceil(pagination.count / 20) || 1);
      }
    } catch {
      setCatalogRules([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
    setPage(1);
  };

  const isSelected = (rule: CatalogRule) => selectedRules.some((r) => r.id === rule.id);

  const toggleSelect = (rule: CatalogRule) => {
    if (isSelected(rule)) {
      setSelectedRules((prev) => prev.filter((r) => r.id !== rule.id));
      setConfiguredArgs((prev) => {
        const copy = { ...prev };
        delete copy[rule.id];
        return copy;
      });
    } else {
      setSelectedRules((prev) => [...prev, rule]);
      // Initialize default args
      const defaults: Record<string, any> = {};
      for (const arg of rule.args) {
        if (arg.name === 'severity' || arg.name === 'autoupdate' || arg.name === 'version') continue;
        defaults[arg.name] = arg.default ?? '';
      }
      setConfiguredArgs((prev) => ({ ...prev, [rule.id]: defaults }));
    }
  };

  const handleConfigure = () => {
    if (selectedRules.length > 0) setStep('configure');
  };

  const handleArgChange = (ruleId: number, argName: string, value: any) => {
    setConfiguredArgs((prev) => ({
      ...prev,
      [ruleId]: { ...prev[ruleId], [argName]: value },
    }));
  };

  const handleRemoveFromConfigure = (ruleId: number) => {
    setSelectedRules((prev) => prev.filter((r) => r.id !== ruleId));
    setConfiguredArgs((prev) => {
      const copy = { ...prev };
      delete copy[ruleId];
      return copy;
    });
    if (selectedRules.length <= 1) setStep('browse');
  };

  const handleAddAll = () => {
    const newRules: SuggestedRule[] = selectedRules.map((rule) => {
      const args = configuredArgs[rule.id] || {};
      const col = args.column || '';
      return {
        rule_name: rule.rule,
        display_name: col ? `${col}: ${rule.description}` : rule.description,
        column_name: typeof col === 'string' ? col : '',
        kwargs: { ...args },
        category: rule.category || 'manual',
        confidence: 'high' as const,
        reason: 'Manually added from rule catalog',
        source: 'profiler' as const,
        table_name: tableName,
        auto_suggested: false,
        enabled: true,
        package: rule.package,
      };
    });
    onAddRules(newRules);
    handleClose();
  };

  const handleClose = () => {
    setStep('browse');
    setSelectedRules([]);
    setConfiguredArgs({});
    setSearchQuery('');
    setPage(1);
    setExpandedRule(null);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-cargo-surface border border-cargo-border rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-cargo-border">
          <div>
            <h2 className="text-lg font-semibold text-cargo-text">
              {step === 'browse' ? 'Add Rules from Catalog' : 'Configure Rules'}
            </h2>
            <p className="text-xs text-cargo-muted mt-0.5">
              {step === 'browse'
                ? 'Search and select rules to add'
                : `Configure parameters for ${selectedRules.length} selected rule(s)`}
            </p>
          </div>
          <button onClick={handleClose} className="text-cargo-muted hover:text-cargo-text">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {step === 'browse' ? (
            <BrowseStep
              catalogRules={catalogRules}
              loading={loading}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              selectedRules={selectedRules}
              isSelected={isSelected}
              toggleSelect={toggleSelect}
              expandedRule={expandedRule}
              setExpandedRule={setExpandedRule}
              page={page}
              totalPages={totalPages}
              setPage={setPage}
            />
          ) : (
            <ConfigureStep
              selectedRules={selectedRules}
              configuredArgs={configuredArgs}
              onArgChange={handleArgChange}
              onRemove={handleRemoveFromConfigure}
              columns={columns}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-cargo-border">
          <div className="text-sm text-cargo-muted">
            {selectedRules.length} rule(s) selected
          </div>
          <div className="flex gap-3">
            {step === 'configure' && (
              <button
                onClick={() => setStep('browse')}
                className="px-4 py-2 text-sm text-cargo-muted hover:text-cargo-text transition-colors"
              >
                Back to Catalog
              </button>
            )}
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm text-cargo-muted hover:text-cargo-text transition-colors"
            >
              Cancel
            </button>
            {step === 'browse' ? (
              <button
                onClick={handleConfigure}
                disabled={selectedRules.length === 0}
                className="flex items-center gap-2 bg-cargo-accent hover:bg-cargo-accent-hover text-white
                           text-sm font-medium px-5 py-2 rounded-lg transition-colors disabled:opacity-30"
              >
                Configure Selected ({selectedRules.length})
              </button>
            ) : (
              <button
                onClick={handleAddAll}
                disabled={selectedRules.length === 0}
                className="flex items-center gap-2 bg-cargo-success hover:bg-cargo-success/80 text-white
                           text-sm font-medium px-5 py-2 rounded-lg transition-colors disabled:opacity-30"
              >
                <Check className="w-4 h-4" />
                Add {selectedRules.length} Rule(s)
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Browse / Search step ───

function BrowseStep({
  catalogRules, loading, searchQuery, onSearchChange, selectedRules,
  isSelected, toggleSelect, expandedRule, setExpandedRule, page, totalPages, setPage,
}: {
  catalogRules: CatalogRule[];
  loading: boolean;
  searchQuery: string;
  onSearchChange: (val: string) => void;
  selectedRules: CatalogRule[];
  isSelected: (r: CatalogRule) => boolean;
  toggleSelect: (r: CatalogRule) => void;
  expandedRule: number | null;
  setExpandedRule: (id: number | null) => void;
  page: number;
  totalPages: number;
  setPage: (p: number) => void;
}) {
  return (
    <div className="space-y-3">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cargo-muted" />
        <input
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search rules by name (e.g. not_be_null, values_to_be_between)..."
          className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-cargo-panel border border-cargo-border
                     text-cargo-text placeholder-cargo-muted/50 text-sm
                     focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
        />
      </div>

      {/* Selected tags */}
      {selectedRules.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedRules.map((r) => (
            <span
              key={r.id}
              className="inline-flex items-center gap-1 px-2 py-1 bg-cargo-accent/15 text-cargo-accent
                         rounded text-xs font-mono cursor-pointer hover:bg-cargo-accent/25"
              onClick={() => toggleSelect(r)}
            >
              {r.rule.replace('expect_', '').substring(0, 30)}
              <X className="w-3 h-3" />
            </span>
          ))}
        </div>
      )}

      {/* Rule list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-cargo-accent" />
          <span className="ml-2 text-cargo-muted text-sm">Loading rule catalog...</span>
        </div>
      ) : catalogRules.length === 0 ? (
        <p className="text-cargo-muted text-center py-8 text-sm">No rules found.</p>
      ) : (
        <div className="space-y-1 max-h-[40vh] overflow-y-auto">
          {catalogRules.map((rule) => {
            const selected = isSelected(rule);
            const expanded = expandedRule === rule.id;
            return (
              <div key={rule.id} className="bg-cargo-panel border border-cargo-border rounded-lg">
                <div className="flex items-center gap-3 px-4 py-3">
                  {/* Expand */}
                  <button
                    onClick={() => setExpandedRule(expanded ? null : rule.id)}
                    className="text-cargo-muted hover:text-cargo-text flex-shrink-0"
                  >
                    {expanded
                      ? <ChevronDown className="w-4 h-4" />
                      : <ChevronRight className="w-4 h-4" />}
                  </button>
                  {/* Name + description */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-cargo-text font-mono truncate">{rule.rule}</p>
                    <p className="text-xs text-cargo-muted truncate">{rule.description}</p>
                  </div>
                  {/* Category badge */}
                  <span className="text-xs px-2 py-0.5 bg-cargo-panel rounded text-cargo-muted flex-shrink-0">
                    {rule.category}
                  </span>
                  {/* Select button */}
                  <button
                    onClick={() => toggleSelect(rule)}
                    className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                      selected
                        ? 'bg-cargo-accent text-white'
                        : 'bg-cargo-panel text-cargo-muted hover:text-cargo-text hover:bg-cargo-hover'
                    }`}
                  >
                    {selected ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                  </button>
                </div>
                {/* Expanded details */}
                {expanded && (
                  <div className="px-4 pb-3 pl-11 text-xs space-y-1">
                    <p className="text-cargo-muted">{rule.long_description || rule.description}</p>
                    <div className="flex gap-4 text-cargo-muted/70">
                      <span>Package: {rule.package}</span>
                      <span>Status: {rule.status}</span>
                      {rule.is_multi_column && <span className="text-cargo-warning">Multi-column</span>}
                    </div>
                    {rule.args.filter(a => a.name !== 'severity' && a.name !== 'autoupdate' && a.name !== 'version').length > 0 && (
                      <div className="mt-1">
                        <span className="text-cargo-muted/70">Parameters: </span>
                        {rule.args
                          .filter(a => a.name !== 'severity' && a.name !== 'autoupdate' && a.name !== 'version')
                          .map((a) => (
                            <span key={a.name} className={`mr-2 ${a.mandatory === 'yes' ? 'text-cargo-accent' : 'text-cargo-muted/60'}`}>
                              {a.name}{a.mandatory === 'yes' ? '*' : ''}
                            </span>
                          ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="px-3 py-1 text-xs bg-cargo-panel rounded text-cargo-muted hover:text-cargo-text disabled:opacity-30"
          >
            Prev
          </button>
          <span className="text-xs text-cargo-muted">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1 text-xs bg-cargo-panel rounded text-cargo-muted hover:text-cargo-text disabled:opacity-30"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Configure parameters step ───

function ConfigureStep({
  selectedRules, configuredArgs, onArgChange, onRemove, columns,
}: {
  selectedRules: CatalogRule[];
  configuredArgs: Record<number, Record<string, any>>;
  onArgChange: (ruleId: number, argName: string, value: any) => void;
  onRemove: (ruleId: number) => void;
  columns: string[];
}) {
  const [showOptional, setShowOptional] = useState<Record<number, boolean>>({});

  return (
    <div className="space-y-4">
      {selectedRules.map((rule) => {
        const args = configuredArgs[rule.id] || {};
        const visibleArgs = rule.args.filter(
          (a) => a.name !== 'severity' && a.name !== 'autoupdate' && a.name !== 'version'
        );
        const mandatoryArgs = visibleArgs.filter((a) => a.mandatory === 'yes');
        const optionalArgs = visibleArgs.filter((a) => a.mandatory !== 'yes');
        const showOpt = showOptional[rule.id] || false;

        return (
          <div key={rule.id} className="bg-cargo-panel border border-cargo-border rounded-xl p-4">
            {/* Rule header */}
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-cargo-text font-mono">{rule.rule}</p>
                <p className="text-xs text-cargo-muted mt-0.5">{rule.description}</p>
                <p className="text-xs text-cargo-muted/60 mt-0.5">Package: {rule.package}</p>
              </div>
              <button onClick={() => onRemove(rule.id)} className="text-cargo-muted hover:text-cargo-error">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            {/* Mandatory args */}
            {mandatoryArgs.length > 0 && (
              <div className="space-y-3">
                {mandatoryArgs.map((arg) => (
                  <ArgField
                    key={arg.name}
                    arg={arg}
                    value={args[arg.name]}
                    onChange={(val) => onArgChange(rule.id, arg.name, val)}
                    columns={columns}
                    isMultiColumn={rule.is_multi_column}
                  />
                ))}
              </div>
            )}

            {/* Optional toggle */}
            {optionalArgs.length > 0 && (
              <div className="mt-3">
                <button
                  onClick={() => setShowOptional((prev) => ({ ...prev, [rule.id]: !showOpt }))}
                  className="text-xs text-cargo-accent hover:underline"
                >
                  {showOpt ? 'Hide' : 'Show'} optional parameters ({optionalArgs.length})
                </button>
                {showOpt && (
                  <div className="space-y-3 mt-3">
                    {optionalArgs.map((arg) => (
                      <ArgField
                        key={arg.name}
                        arg={arg}
                        value={args[arg.name]}
                        onChange={(val) => onArgChange(rule.id, arg.name, val)}
                        columns={columns}
                        isMultiColumn={rule.is_multi_column}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Individual arg field ───

function ArgField({
  arg, value, onChange, columns, isMultiColumn,
}: {
  arg: CatalogRuleArg;
  value: any;
  onChange: (val: any) => void;
  columns: string[];
  isMultiColumn: boolean;
}) {
  const supportedStr = (arg.supported_values || []).map((s) => s.toLowerCase()).join(',');
  const isColumnField = arg.name === 'column' || arg.name === 'column_list' || arg.name === 'column_A' || arg.name === 'column_B';

  // Column dropdown (with autocomplete)
  if (isColumnField && columns.length > 0) {
    if (arg.name === 'column_list' || (arg.name === 'column' && isMultiColumn)) {
      // Multi-select for column_list
      const selectedCols: string[] = Array.isArray(value) ? value : value ? [value] : [];
      return (
        <div>
          <ArgLabel arg={arg} />
          <div className="flex flex-wrap gap-1 mb-1">
            {selectedCols.map((col) => (
              <span key={col} className="inline-flex items-center gap-1 px-2 py-0.5 bg-cargo-accent/15 text-cargo-accent rounded text-xs">
                {col}
                <button onClick={() => onChange(selectedCols.filter((c) => c !== col))}><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <select
            value=""
            onChange={(e) => {
              if (e.target.value && !selectedCols.includes(e.target.value)) {
                onChange([...selectedCols, e.target.value]);
              }
            }}
            className="w-full px-3 py-2 rounded-lg bg-cargo-surface border border-cargo-border text-cargo-text text-sm
                       focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
          >
            <option value="">Select column...</option>
            {columns.filter((c) => !selectedCols.includes(c)).map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      );
    }
    // Single column dropdown
    return (
      <div>
        <ArgLabel arg={arg} />
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-cargo-surface border border-cargo-border text-cargo-text text-sm
                     focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
        >
          <option value="">Select column...</option>
          {columns.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>
    );
  }

  // Boolean
  if (supportedStr === 'boolean' || supportedStr === 'bool') {
    return (
      <div>
        <ArgLabel arg={arg} />
        <select
          value={value === true || value === 'true' ? 'true' : value === false || value === 'false' ? 'false' : ''}
          onChange={(e) => onChange(e.target.value === 'true')}
          className="w-full px-3 py-2 rounded-lg bg-cargo-surface border border-cargo-border text-cargo-text text-sm
                     focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
        >
          <option value="">--</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      </div>
    );
  }

  // Custom dropdown (value_list)
  if (arg.type === 'Custom_Dropdown' && arg.value_list) {
    return (
      <div>
        <ArgLabel arg={arg} />
        <select
          value={value || arg.default || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-cargo-surface border border-cargo-border text-cargo-text text-sm
                     focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
        >
          <option value="">--</option>
          {arg.value_list.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      </div>
    );
  }

  // JSON / Array -> textarea
  if (supportedStr.includes('json') || supportedStr.includes('array')) {
    return (
      <div>
        <ArgLabel arg={arg} />
        <textarea
          value={typeof value === 'string' ? value : value ? JSON.stringify(value) : ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={supportedStr.includes('array') ? '[Array]' : '{JSON}'}
          rows={2}
          className="w-full px-3 py-2 rounded-lg bg-cargo-surface border border-cargo-border text-cargo-text text-sm
                     font-mono focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none resize-y"
        />
      </div>
    );
  }

  // Default: text input
  return (
    <div>
      <ArgLabel arg={arg} />
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={arg.default != null ? String(arg.default) : arg.type}
        className="w-full px-3 py-2 rounded-lg bg-cargo-surface border border-cargo-border text-cargo-text text-sm
                   focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
      />
    </div>
  );
}

function ArgLabel({ arg }: { arg: CatalogRuleArg }) {
  return (
    <label className="flex items-center gap-1 text-xs font-medium text-cargo-muted mb-1">
      {arg.name}
      {arg.mandatory === 'yes' && <span className="text-cargo-error">*</span>}
      <span className="text-cargo-muted/40 ml-1">({arg.type})</span>
    </label>
  );
}

export default AddRuleModal;
